"use client";

import { ModuleStatsList } from "@lib/types";
import { motion } from "motion/react";
import { useState } from "react";

import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { icon } from "@/components/ui/icons";
import { cn } from "@/lib/ui/cn";

import { Separator } from "../../ui/separator";

const AddIcon = icon("Plus");
const KeyboardArrowDownIcon = icon("ChevronDown");

type StatsListItemsProps = {
  items: ModuleStatsList["items"];
};

export const StatsListItems = ({ items }: StatsListItemsProps) => {
  const [activeItemKey, setActiveItemKey] = useState<ModuleStatsList["items"][number]["_key"]>(
    items[0]._key,
  );

  return (
    <div className={cn("gap-gap-element-md desktop:flex-row flex flex-col")}>
      {items?.map((item, index) => {
        const isActive = item._key === activeItemKey;

        return (
          <motion.div
            key={item._key}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", bounce: 0, duration: 2, delay: index * 0.5 }}
            onHoverStart={() => setActiveItemKey(item._key)}
            onClick={() => setActiveItemKey(item._key)}
            className="flex"
          >
            <div
              className={cn(
                "p-padding-element-md gap-gap-element-lg relative flex max-w-full min-w-full flex-col self-stretch rounded-lg",
                "desktop:h-139",
                "pointer-events-none overflow-hidden transition-all",
                isActive
                  ? [
                      "h-auto border border-transparent bg-white",
                      "desktop:max-w-165 desktop:shadow-[0_24.721px_32.257px_0_rgba(45,247,169,0.13),0_10.268px_23.4px_0_rgba(0,229,141,0.12),0_3.714px_24.85px_0_rgba(0,229,141,0.05)]",
                    ]
                  : [
                      "border-indigo-lighter h-16 border",
                      "tablet:h-[89px]",
                      "desktop:max-w-52 bg-[linear-gradient(180deg,rgba(189,212,252,0.2)_0%,rgba(178,178,179,0.2)_100%)]",
                    ],
              )}
            >
              <motion.div
                initial={false}
                animate={{
                  opacity: isActive ? 1 : 0,
                }}
                transition={{ type: "spring", delay: isActive ? 0.3 : 0, duration: 0.3 }}
                className="tablet:flex-row desktop:flex-col gap-gap-element-lg flex flex-col"
              >
                <ResponsiveImage
                  src={item.image}
                  width={628}
                  height={260}
                  className={cn(
                    "aspect-157/65 w-full rounded-lg object-contain",
                    "tablet:object-cover tablet:aspect-square tablet:w-[255px]",
                    "desktop:w-full desktop:aspect-157/65 desktop:object-contain",
                  )}
                  imageOptions={{ desktopWidth: 628 }}
                  useNextImage
                />

                <div className="py-padding-element-md gap-gap-element-md pointer-events-none flex flex-col">
                  <p className="typography-text-large-normal text-neutral-darkest">{item.title}</p>
                  <Separator className="bg-neutral-light" />
                  <p className="typography-text-medium-normal text-neutral-dark">{item.body}</p>
                </div>
              </motion.div>

              <motion.div
                initial={false}
                animate={{
                  opacity: !isActive ? 1 : 0,
                }}
                transition={{ type: "spring", duration: 0.3 }}
                className={cn(
                  "align-center p-padding-element-lg absolute top-0 flex h-full w-full flex-row-reverse justify-between self-center",
                  "desktop:flex-col desktop:w-auto desktop:p-padding-element-xl",
                )}
              >
                <AddIcon className="desktop:block hidden size-8 self-center fill-white" />
                <KeyboardArrowDownIcon className="desktop:hidden size-8 self-center fill-white" />

                <p
                  className={cn(
                    "typography-text-large-normal size-auto self-center text-white",
                    "desktop:bottom-8 desktop:size-8 desktop:-rotate-90 desktop:whitespace-nowrap",
                  )}
                >
                  {item.title}
                </p>
              </motion.div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};
